import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Option = { value: string; label: string };
type Props = {
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  emptyLabel?: string;
  className?: string;
};

/** La soglia riguarda le scelte disponibili, indipendentemente dalla ricerca digitata. */
export function OptionFilter({ emptyLabel = 'Tutti', ...props }: Props) {
  const options = [
    ...new Map(props.options.filter((o) => o.value).map((o) => [o.value, o])).values(),
  ];
  if (options.length > 10)
    return <Autocomplete {...props} options={options} emptyLabel={emptyLabel} />;
  return (
    <select
      aria-label={props.label}
      className={props.className}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {props.value && !options.some((o) => o.value === props.value) && (
        <option value={props.value}>{props.value}</option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

const normalize = (s: string) =>
  s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('it').trim();

function Autocomplete({ label, value, options, onChange, emptyLabel = 'Tutti', className }: Props) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    width: 0,
    maxHeight: 240,
    transform: 'none',
  });
  const selected = options.find((o) => o.value === value)?.label ?? value;
  const term = normalize(query ?? '');
  const matches = options.filter((o) => normalize(`${o.label} ${o.value}`).includes(term));
  const suggestions = [{ value: '', label: emptyLabel }, ...matches.slice(0, 50)];
  const close = () => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
  };
  const pick = (option: Option) => {
    onChange(option.value);
    close();
  };

  // Il portale evita che lo scorrimento orizzontale della tabella tagli i suggerimenti.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = input.current!.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 8;
      const above = rect.top - 8;
      const downward = below >= Math.min(240, suggestions.length * 36 + 12) || below >= above;
      const width = Math.min(Math.max(rect.width, 240), window.innerWidth - 16);
      setPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: downward ? rect.bottom + 4 : rect.top - 4,
        width,
        maxHeight: Math.max(0, Math.min(240, downward ? below : above)),
        transform: downward ? 'none' : 'translateY(-100%)',
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, suggestions.length]);

  useEffect(() => {
    if (open && active >= 0)
      document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, id, open]);

  return (
    <div className="relative min-w-36">
      <input
        ref={input}
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-activedescendant={open && suggestions[active] ? `${id}-${active}` : undefined}
        autoComplete="off"
        className={`${className ?? ''} w-full !pr-7`}
        placeholder={emptyLabel}
        value={query ?? selected}
        onFocus={() => {
          setOpen(true);
          setActive(-1);
        }}
        onClick={() => setOpen(true)}
        onBlur={close}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(-1);
          if (!e.target.value) onChange('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            setActive((previous) => {
              const current = previous < suggestions.length ? previous : -1;
              return e.key === 'ArrowDown'
                ? (current + 1) % suggestions.length
                : (current <= 0 ? suggestions.length : current) - 1;
            });
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && suggestions[active]) pick(suggestions[active]!);
            else if (open && matches.length === 1) pick(matches[0]!);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
        }}
      />
      {value && (
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-ink/55"
          aria-label={`Rimuovi filtro: ${label}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onChange('');
            close();
          }}
        >
          ×
        </button>
      )}
      {open &&
        createPortal(
          <div
            className="fixed z-[100] overflow-y-auto rounded-lg border border-black/15 bg-white py-1 text-left text-sm font-normal text-ink shadow-lg"
            style={position}
          >
            <div id={id} role="listbox" aria-label={label}>
              {suggestions.map((o, index) => (
                <div
                  id={`${id}-${index}`}
                  key={o.value}
                  role="option"
                  aria-selected={o.value === value}
                  className={`cursor-pointer px-3 py-2 ${index === active ? 'bg-accent/10' : 'hover:bg-black/5'}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                >
                  {o.label}
                </div>
              ))}
            </div>
            {matches.length === 0 && (
              <p role="status" className="px-3 py-2 text-ink/55">
                Nessun risultato
              </p>
            )}
            {matches.length > 50 && (
              <p role="status" className="px-3 py-2 text-xs text-ink/55">
                Prime 50 di {matches.length} opzioni. Scrivi per restringere la ricerca.
              </p>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
